// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Resolve a regulatory framework record to a display string.
 *
 * ## Why this exists
 *
 * `display_name` is present on all 202 corpus frameworks — 117 as a plain
 * string and 85 as a locale map. Every reader therefore has to handle both
 * shapes, and by the 2026-08-10 review there were **seven** private
 * implementations of that one rule in `src/backend`:
 *
 * | site | shape | fallback |
 * |---|---|---|
 * | `data-mapping/ropa-autofix-service.ts` `displayNameOf` | `en` only | `''` |
 * | `ai-governance/ai-governance-service.ts` `nameOf` | `en` only | code |
 * | `ai-governance/ai-governance-collector.ts` `frameworkName` | locale → `en` | code |
 * | `localization/sector-overlay-service.ts` (inline) | **string only** | code |
 * | `localization/framework-capability-registry.ts` `toCapable` | narrowing only | code |
 * | `localization/regulatory-framework-service.ts` `auditEntityName` | `en` | code |
 * | `onboarding/onboarding-maturity-service.ts` (inline) | `en` | code |
 *
 * Seven readers of a two-shape field is seven places for "handles both shapes"
 * to stop being true, and the variants had already drifted: two of them
 * degraded a locale map to the raw framework code, which is the exact defect
 * Sprint 64.18.0 existed to remove.
 *
 * This is the backend counterpart of the frontend's
 * `resolveFrameworkDisplayName` (`src/frontend/src/utils/framework-display.ts`).
 * The two are deliberately *not* the same function: the frontend one consults
 * curated `t.localization.frameworks_detail.label_<CODE>` copy first and needs a
 * translation bundle to do it. Backend callers have no bundle, so this one
 * resolves pack content only.
 *
 * ## The fallback is the code, never `''`
 *
 * An unnamed framework is still identifiable — the code is what the record must
 * be re-readable by. Silence tells the reader the platform lost the row.
 * `''` is returned only when the record carries neither a name nor a code.
 */
import { extractLocale } from './locale-extraction.js';
/**
 * English (or requested-locale) display name of a framework record, falling
 * back to its code. Safe to call on any value — never throws.
 *
 * Resolution order:
 * 1. `display_name` through {@link extractLocale} for `locale`.
 * 2. `name` through {@link extractLocale} for `locale` — the shape
 *    `RegulatoryFrameworkService.create()` writes.
 * 3. `framework_code`, then `code`.
 * 4. `''`.
 *
 * @param record The framework document, manifest entry or payload.
 * @param locale Desired locale. Defaults to `'en'`, which is the right answer
 *   for audit rows and other historical records: they are read later by
 *   somebody other than whoever acted, so they must not be pinned to the
 *   actor's locale.
 *
 * @example
 *   frameworkDisplayName({ display_name: 'Chemicals Law' })            // 'Chemicals Law'
 *   frameworkDisplayName({ display_name: { en: 'Chemicals Law', vi: 'Luật Hóa chất' } })
 *                                                                     // 'Chemicals Law'
 *   frameworkDisplayName({ display_name: { en: 'X', vi: 'Y' } }, 'vi') // 'Y'
 *   frameworkDisplayName({ display_name: {}, framework_code: 'ZZ_X' }) // 'ZZ_X'
 *   frameworkDisplayName(null)                                         // ''
 */
export function frameworkDisplayName(record, locale = 'en') {
    const r = (record ?? {});
    for (const field of [r.display_name, r.name]) {
        const resolved = extractLocale(field, locale);
        if (typeof resolved === 'string' && resolved.trim())
            return resolved;
    }
    for (const code of [r.framework_code, r.code]) {
        if (typeof code === 'string' && code.trim())
            return code;
    }
    return '';
}
//# sourceMappingURL=framework-display-name.js.map